// eslint-disable-next-line no-restricted-imports
import { getEsbuildBuildEnvVar, getEsbuildRuntimeEnvVar } from '@wizbii-utils/angular/core';
import { LangEnum, LocaleEnum } from '@wizbii/utils/models';
// eslint-disable-next-line no-restricted-imports
import { buildConfig } from './build-config';

// eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
const packageJsonVersionAtBuild = require('../../package.json').version;

const getRuntimeEnvVar = (name: string): string => getEsbuildRuntimeEnvVar(name, window) as string;
const getBuildEnvVar = (name: string): string => getEsbuildBuildEnvVar(name, window) as string;

export const getBaseHref = (): string => {
  const baseHrefEnvVar = getRuntimeEnvVar('BASE_HREF');
  const baseHref = baseHrefEnvVar ? baseHrefEnvVar : '';
  return baseHref.startsWith('/') ? baseHref : `/${baseHref}`;
};

export const getBaseHrefWithTrailingSlash = (): string => {
  const baseHref = getBaseHref();
  return baseHref.endsWith('/') ? baseHref : `${baseHref}/`;
};

// @ts-ignore
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
const production: boolean = buildConfig === 'production';
const applicationId = 'fibii';
const platform: string = getRuntimeEnvVar('PLATFORM');
const wizbiiDomain: string = getRuntimeEnvVar('WIZBII_ENV_FQDN');
const wizbiiDomainIt: string = getRuntimeEnvVar('WIZBII_ENV_FQDN_ITALY');
const wizbiiDomainEs: string = getRuntimeEnvVar('WIZBII_ENV_FQDN_SPAIN');
const apiDomain = getRuntimeEnvVar('API_DOMAIN');
const apiPlatform = getRuntimeEnvVar('API_PLATFORM');
const scheme = getRuntimeEnvVar('HTTP_SCHEME');

const algoliaAppId = getRuntimeEnvVar('ALGOLIA_APP_ID');
const algoliaApiKey = getRuntimeEnvVar('ALGOLIA_API_KEY');

const lang: keyof typeof LangEnum = getBuildEnvVar('LANG') as keyof typeof LangEnum;
const locale: LocaleEnum = getBuildEnvVar('LOCALE') as LocaleEnum;

const deployAssetsUrl = production
  ? `${scheme}://storage.googleapis.com/wizbii-application-assets/fibii/${getBuildEnvVar('CI_COMMIT_SHA')}/${lang}/assets`
  : null;

const domainByLang = {
  [LangEnum.fr]: `https://www.${wizbiiDomain}`,
  [LangEnum.es]: `https://www.${wizbiiDomainEs}`,
  [LangEnum.it]: `https://www.${wizbiiDomainIt}`,
};

const envFqdnByLang = {
  [LangEnum.fr]: wizbiiDomain,
  [LangEnum.es]: wizbiiDomainEs,
  [LangEnum.it]: wizbiiDomainIt,
};

const envFqdnByLocale = {
  [LocaleEnum.fr_FR]: wizbiiDomain,
  [LocaleEnum.es_ES]: wizbiiDomainEs,
  [LocaleEnum.it_IT]: wizbiiDomainIt,
};

const googleSendToByLang = {
  [LangEnum.fr]: { redirection: '_b_6CKX-qbMYEJDT_qAD', addToCart: 'wkCKCJPEh-QBEJDT_qAD' },
  [LangEnum.it]: { redirection: 'xDMcCKX9pfIZEJ7H0r0o', addToCart: 'xDMcCKX9pfIZEJ7H0r0o' },
  [LangEnum.es]: { redirection: 'jsGQCOCByccaEMDEz70o', addToCart: 'jsGQCOCByccaEMDEz70o' },
};

const apiDomains = {
  envFqdn: envFqdnByLang[lang] as string,
  googleStorage: 'https://storage.googleapis.com',
  imaginary: `${domainByLang[lang]}/_api/imaginary`,
  accountApi: `${domainByLang[lang]}/_api/account-api`,
  abTestApi: `https://abtest-api.${wizbiiDomain}`,
  account: `${domainByLang[lang]}/account`,
  authentication: `${domainByLang[lang]}/_api/auth-api`,
  analytics: `${domainByLang[lang]}/_api/analytiks-api`,
  content: `${domainByLang[lang]}/_api/content-api`,
  consent: `${domainByLang[lang]}/_api/consent-api`,
  contact: `${domainByLang[lang]}/_api/contact-api-v2`,
};

export const environment = {
  cookieDomains: window.location.hostname === 'localhost' ? 'localhost' : envFqdnByLocale,
  applicationId,
  platform,
  production,
  appUrl:
    platform === 'local'
      ? `http://localhost:4200`
      : lang === 'fr'
        ? `${scheme}://money.${wizbiiDomain}`
        : `${domainByLang[lang]}/money`,
  contactEmailKey: {
    base: 'money',
    premium: 'money_premium',
  },
  version: (production ? packageJsonVersionAtBuild : getBuildEnvVar('GIT_BRANCH_NAME')) as string,
  i18n: {
    lang: LangEnum[lang],
    locale,
    country: locale.split('_')[1],
    domainsByLocale: {
      it_IT: wizbiiDomainIt,
      es_ES: wizbiiDomainEs,
    },
  },
  domain: {
    wizbii: wizbiiDomain,
  },
  bugsnag: {
    key: '9631b4ce2e6d998312ae665b965f891b',
  },
  envFqdn: apiDomains.envFqdn,
  api: {
    account: apiDomains.accountApi,
    authentication: apiDomains.authentication,
    contact: apiDomains.contact,
    content: apiDomains.content,
    consent: apiDomains.consent,
    domain: apiDomains.envFqdn,
    analytics: apiDomains.analytics,
    place: `${scheme}://place.${apiDomain}`,
    bankingProcessor: `${scheme}://banking-processor.api.${apiDomain}`,
    platform: apiPlatform,
    suggestion: `${scheme}://suggestion.api.${apiDomain}`,
    file: `${scheme}://file.${apiDomain}`,
    fibii: `${scheme}://fibii.api.${apiDomain}`,
    chatbot: `${scheme}://ia-server-api.${apiDomain}`,
    imaginary: `${scheme}://imaginary.${apiDomain}`,
    abtest: apiDomains.abTestApi,
    strapi:
      platform === 'prod'
        ? 'https://money.strapi.wizbii.team/api'
        : `https://money.strapi.team.${platform}.k8s.wizbii.us/api`,
  },
  urls: {
    account: {
      base: apiDomains.account,
      signIn: `${apiDomains.account}/sign-in?app-id=${applicationId}&redirect=`,
      signUp: `${apiDomains.account}/sign-up?app-id=${applicationId}&redirect=`,
      logout: `${apiDomains.account}/logout?app-id=${applicationId}&redirect=`,
    },
    googleStorage: 'https://storage.googleapis.com',
  },
  awinMerchantId: '',
  googleSendTo: googleSendToByLang[lang] as { redirection: string; addToCart: string },
  deployAssetsUrl,
  algolia: {
    applicationId: algoliaAppId,
    apiKey: algoliaApiKey,
  },
  wizbiiFiles: getRuntimeEnvVar('WIZBII_FILES_GCS_BUCKET'),
  abTest: {
    fr_FR: [{ name: 'new-user-journey', campaignId: 'money-new-user-journey' }],
    it_IT: [{ name: 'new-user-journey', campaignId: 'money-new-user-journey' }],
    es_ES: [{ name: 'new-user-journey', campaignId: 'money-new-user-journey' }],
  },
  nps: {
    [LocaleEnum.fr_FR]: {
      devSurvey: 'nps_survey_test',
      prodSurvey: 'nps_survey_money_landing',
    },
    [LocaleEnum.it_IT]: {
      devSurvey: 'nps_survey_test_it',
      prodSurvey: 'nps_survey_money_landing_it',
    },
    [LocaleEnum.es_ES]: {
      devSurvey: 'nps_survey_test_es',
      prodSurvey: 'nps_survey_money_landing_es',
    },
  },
  baseHref: getBaseHrefWithTrailingSlash(),
};
